import { useEffect, useRef, useState } from 'react';
import { Bell, Smartphone } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardTitle } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Switch } from 'components/ui/switch';
import { useAuth } from 'contexts/AuthContext';
import { mobileApi, type MobilePreferences, type MobileStatus } from 'services/api/mobileClient';
import { disableNativePush, getInstallationId, MOBILE_REGISTRATION_CHANGED, registerNativePush, testLocalNotification } from './mobileNotifications';
import { isNativeMobile } from './nativeBridge';
import { getAuthGeneration } from 'utils/auth';
import { useNativeCapabilities } from './useNativeCapabilities';

export function MobileNotificationSettings() {
  const { user } = useAuth();
  if (!user || !isNativeMobile()) return null;
  return <AccountNotificationSettings key={`${user.id}:${getAuthGeneration()}`} userId={user.id} />;
}

function AccountNotificationSettings({ userId }: { userId: number }) {
  const { capabilities } = useNativeCapabilities();
  const [status, setStatus] = useState<MobileStatus | null>(null);
  const [preferences, setPreferences] = useState<MobilePreferences | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [registered, setRegistered] = useState(false);
  const [testRequest, setTestRequest] = useState<string | null>(null);
  const mounted = useRef(true);
  const generation = useRef(getAuthGeneration());
  const isCurrent = () => mounted.current && generation.current === getAuthGeneration();
  useEffect(() => {
    let current = true;
    mounted.current = true;
    let refreshId = 0;
    const refresh = async () => {
      const id = ++refreshId;
      try {
        const next = await mobileApi.status(getInstallationId());
        if (current && generation.current === getAuthGeneration() && id === refreshId) {
          setStatus(next);
          setRegistered(next.device_registered === true);
        }
      } catch { if (current) setError('Device reminder status is unavailable. Reopen this page to try again.'); }
    };
    void refresh();
    void mobileApi.preferences().then((next) => {
      if (current && generation.current === getAuthGeneration()) setPreferences(next);
    }).catch(() => { if (current) setError('Reminder settings are unavailable. Reopen this page to try again.'); });
    window.addEventListener(MOBILE_REGISTRATION_CHANGED, refresh);
    return () => {
      current = false;
      mounted.current = false;
      window.removeEventListener(MOBILE_REGISTRATION_CHANGED, refresh);
    };
  }, []);
  const configured = Boolean(capabilities?.remotePush && status?.enabled && status.providers[capabilities.platform === 'ios' ? 'apns' : 'fcm']);
  const run = async (action: () => Promise<void>, success: string) => {
    setBusy(true); setMessage(''); setError('');
    try { await action(); if (isCurrent()) setMessage(success); }
    catch (failure) { if (isCurrent()) setError(failure instanceof Error ? failure.message : 'Unable to save this change. Please try again.'); }
    finally { if (isCurrent()) setBusy(false); }
  };
  const sendRemoteTest = async () => {
    // Keep this ID when the response is lost (for example, while backgrounding).
    // Retrying checks/requeues the same event instead of delivering a second test.
    const requestId = testRequest || crypto.randomUUID();
    setTestRequest(requestId);
    let result;
    try {
      result = await mobileApi.testNotification(getInstallationId(), requestId);
    } catch (failure) {
      const statusCode = (failure as { response?: { status?: number } })?.response?.status;
      if (!statusCode || statusCode >= 500) throw new Error('We could not confirm the remote test. It may still arrive. Retry this test when connected; it will use the same request.');
      if (isCurrent()) setTestRequest(null);
      throw new Error('The remote test could not be requested. Check that reminders are enabled and try again.');
    }
    if (['failed', 'cancelled', 'skipped'].includes(result.status)) {
      if (isCurrent()) setTestRequest(null);
      throw new Error('The remote test was not delivered. Reconnect reminders and try again.');
    }
    if (!result.queued && result.status === 'pending') {
      throw new Error('The remote test is saved and waiting to be sent. You can retry this same test when connected.');
    }
    if (isCurrent()) setTestRequest(null);
  };
  return (
    <Card maxWidth="none" className="mb-6" data-testid="mobile-notification-settings">
      <CardTitle title="On this device" />
      <CardContent className="items-start space-y-4">
        <p className="text-sm text-gray-600">Enable reminders on this device, then choose reminders for your account. Your email and text preferences are separate.</p>
        <div className="flex flex-wrap gap-3">
          <Button disabled={busy} variant="secondary" onClick={() => void run(testLocalNotification, 'A test notification was requested. Check your device’s notification center.')}>
            <Smartphone className="mr-2 h-4 w-4" />Try a notification
          </Button>
          <Button disabled={busy || !configured} onClick={() => void run(async () => {
            if (!capabilities) return;
            await registerNativePush(capabilities, userId);
            if (isCurrent()) setRegistered(true);
          }, 'This device is ready for reminders. Save your choices below.')}>
            <Bell className="mr-2 h-4 w-4" />{registered ? 'Reconnect reminders' : 'Enable reminders'}
          </Button>
        </div>
        {registered && <Button variant="secondary" disabled={busy} onClick={() => void run(async () => {
          await disableNativePush();
          if (isCurrent()) { setRegistered(false); setTestRequest(null); }
        }, 'Reminders are disabled on this device. Your other devices and account preferences are unchanged.')}>
          Disable reminders on this device
        </Button>}
        {configured && registered && <Button variant="secondary" disabled={busy} onClick={() => void run(sendRemoteTest,
          'Your remote test request was accepted. Delivery depends on your notification permissions and connection.')}>
          {testRequest ? 'Retry remote test' : 'Send remote test'}
        </Button>}
        {!configured && <p className="text-sm text-gray-600">Remote reminders are unavailable. You can try a local notification after allowing notifications on this device.</p>}
        {preferences && <form className="w-full space-y-4" onSubmit={(event) => {
          event.preventDefault();
          void run(async () => {
            const { timezone, ...values } = preferences;
            const next = await mobileApi.updatePreferences(values);
            if (isCurrent()) setPreferences(next);
          }, 'Your account reminder preferences were saved.');
        }}>
          <p className="text-sm text-gray-600">These choices apply to all devices where you have enabled reminders.</p>
          {([
            ['daily_checkin_enabled', 'Daily Check-in', 'One reminder for your daily goals and Daily Pulse.'],
            ['meetings_enabled', 'Upcoming meetings', 'Use the lead time in your meeting notification settings.'],
            ['recaps_enabled', 'For next time notes', 'Let me know when my meeting notes are ready.'],
          ] as const).map(([key, title, description]) => <div className="flex items-center justify-between gap-4" key={key}>
            <div><Label htmlFor={`mobile-${key}`}>{title}</Label><p className="text-xs text-gray-600">{description}</p></div>
            <Switch id={`mobile-${key}`} disabled={!configured || !registered || busy} checked={preferences[key]} onCheckedChange={(checked) => setPreferences({ ...preferences, [key]: checked })} />
          </div>)}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {([['reminder_time', 'Daily reminder'], ['quiet_start', 'Quiet hours start'], ['quiet_end', 'Quiet hours end']] as const).map(([key, title]) => <div key={key}>
              <Label htmlFor={`mobile-${key}`}>{title}</Label>
              <Input id={`mobile-${key}`} type="time" required disabled={busy || !configured} value={preferences[key].slice(0, 5)} onChange={(event) => setPreferences({ ...preferences, [key]: event.target.value })} />
            </div>)}
          </div>
          <p className="text-xs text-gray-600">Times use your profile time zone: {preferences.timezone}.</p>
          <Button type="submit" disabled={busy || !configured || !registered}>{busy ? 'Saving…' : 'Save account reminders'}</Button>
        </form>}
        {message && <p role="status" className="text-sm text-green-800">{message}</p>}
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      </CardContent>
    </Card>
  );
}
